import {type BoardCardAddress, BoardDragOverlay, useBoardSelection} from '@github-ui/board-core'
import {Portal} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardCardFrame.module.css'

export type BoardDraggedCardProps<TItem> = {
  /** The same renderer the cells use, so the card in hand is the card on the board. */
  renderCard: (item: TItem) => ReactNode
}

/**
 * The card under the pointer while somebody is dragging one.
 *
 * **The card being dragged does not move**, and this is what makes that read as a drag
 * rather than as nothing happening. A card lives inside a cell that scrolls its own list
 * and draws only the cards on screen, so translating the card would slide it under its own
 * cell's edge instead of across the board. The held card stays where it is and is ringed
 * by `BoardCardFrame`; this copy is what travels.
 *
 * It draws nothing where it is rendered. `BoardDragOverlay` is `position: fixed`, so it
 * goes through a portal rather than into the board's own element, which is a scroll
 * container, leaves the board the only thing in the box a caller put it in, and renders
 * nothing at all on the server.
 *
 * The copy is hidden from assistive technology and taken out of the tab order, by
 * `BoardDragOverlay` rather than here: the card it copies has not moved, so a readable
 * copy announces the same card twice, and a card's title is a link, so a reachable copy is
 * a second tab stop for a card that already has one.
 *
 * `BoardBody` renders one of these, so a board built from `<Board>` or from `BoardBody`
 * has it already. It is exported for a board assembled from these frames by hand.
 */
export function BoardDraggedCard<TItem>({renderCard}: BoardDraggedCardProps<TItem>) {
  return (
    <Portal>
      <BoardDragOverlay renderDraggedCard={card => <DraggedCard card={card} renderCard={renderCard} />} />
    </Portal>
  )
}

function DraggedCard<TItem>({card, renderCard}: {card: BoardCardAddress; renderCard: (item: TItem) => ReactNode}) {
  // `useBoardSelection` warns a card away from it because it wakes on every selection
  // change, and there is exactly one of these on a board rather than one per card.
  // `describeCard` is the only way to reach the caller's item from an address: the board
  // holds `getItemId`, not this.
  const {describeCard} = useBoardSelection()
  const {item} = describeCard<TItem>(card)

  // The card was taken off the board mid-drag, by a fetch that no longer holds it. There
  // is nothing to copy, and the drag itself is still the board's to report.
  if (item === undefined) return null

  // Nothing here hides this copy or takes it out of the tab order: `BoardDragOverlay`
  // wraps whatever this returns in an element that is both, because that is true of every
  // copy rather than a decision this one makes.
  return <div className={clsx(styles.CardFrame, styles.DraggedCard)}>{renderCard(item)}</div>
}
